import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { APP_VERSION, BUILD_ID, compareAppVersion, formatVersion } from '../src/config/version';

export interface AppVersionRecord {
    id: string;
    version: string;
    release_date: string;
    release_notes: string[];
    is_latest: boolean;
    /** Tidak dipakai lagi (dulu selalu = versi rilis). Lihat `is_mandatory`. */
    min_required_version?: string;
    /** Rilis WAJIB: pengguna di bawah versi ini dipaksa memperbarui. */
    is_mandatory?: boolean;
    created_at?: string;
}

/** Isi /version.json — versi yang SUDAH ter-deploy (ditulis saat build). */
export interface DeployedVersionInfo {
    version: string;
    buildId?: string;
    builtAt?: string;
}

/** Selang pengecekan versi deploy saat aplikasi terbuka. */
const VERSION_POLL_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Baca versi yang sudah ter-deploy dari /version.json (selalu dari jaringan).
 * null bila tidak tersedia (mis. mode dev) — pemanggil memakai cadangan.
 */
export const fetchDeployedVersion = async (): Promise<DeployedVersionInfo | null> => {
    try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        // Mode dev / rewrite SPA bisa membalas index.html → bukan JSON
        if (!(res.headers.get('content-type') || '').includes('json')) return null;
        const data = await res.json();
        return data && typeof data.version === 'string' ? (data as DeployedVersionInfo) : null;
    } catch {
        return null;
    }
};

/**
 * Kunci sessionStorage yang mencatat versi TERBARU yang sudah benar-benar
 * terpasang di perangkat (marker di-set TEPAT sebelum reload pada saat user
 * menekan "Perbarui Sekarang").
 *
 * Alur penentuan "update tersedia":
 *   1. Saat aplikasi dimuat, marker ini dibandingkan dengan versi terbaru di
 *      Supabase. Kalau versi terbaru > marker, berarti update belum terpasang
 *      (atau belum pernah diterapkan oleh user) -> modal update muncul.
 *   2. Setelah bundle baru berhasil dimuat (post-update), hook menulis versi
 *      bundel ke Firestore lalu mengisi marker -> versi footer = versi bundel
 *      = marker -> modal tidak muncul lagi dan tombol kuning hilang.
 *   3. Marker juga melindungi dari balapan: reload dari tombol update tidak
 *      akan sempat menulis versi LAMA ke Firestore (syncUserVersion tidak
 *      dijalankan kalau marker sama dengan versi bundel).
 */
const APPLIED_VERSION_KEY = 'app_update_applied_version';

const getAppliedVersion = (): string => {
    try {
        return sessionStorage.getItem(APPLIED_VERSION_KEY) || '';
    } catch {
        return '';
    }
};

const setAppliedVersion = (version: string): void => {
    try {
        sessionStorage.setItem(APPLIED_VERSION_KEY, version);
    } catch {
        // Abaikan kegagalan storage (misal mode incognito)
    }
};

// ============================================================================
// Pembaruan Service Worker (PWA)
// ============================================================================

/** Batas total proses update sebelum reload dipaksa. */
const UPDATE_FAILSAFE_MS = 45000;
/** Batas menunggu browser menemukan SW baru setelah registration.update(). */
const FIND_WORKER_TIMEOUT_MS = 5000;
/** Batas menunggu SW baru selesai memasang (mengunduh file yang berubah). */
const INSTALL_TIMEOUT_MS = 30000;
/** Batas menunggu SW baru mengambil alih halaman setelah SKIP_WAITING. */
const CONTROL_TIMEOUT_MS = 5000;

/** Jalankan promise tapi berhenti menunggu jika melewati deadline (ms). */
const withDeadline = <T,>(promise: Promise<T>, ms: number): Promise<T | 'timeout'> =>
    Promise.race([
        promise,
        new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), ms))
    ]);

/** Tunggu worker mencapai salah satu state; false jika redundant / timeout. */
const waitForWorkerState = (worker: ServiceWorker, states: ServiceWorkerState[], ms: number): Promise<boolean> =>
    new Promise(resolve => {
        if (states.includes(worker.state)) return resolve(true);
        if (worker.state === 'redundant') return resolve(false);
        const done = (ok: boolean) => {
            clearTimeout(timer);
            worker.removeEventListener('statechange', onChange);
            resolve(ok);
        };
        const onChange = () => {
            if (states.includes(worker.state)) done(true);
            else if (worker.state === 'redundant') done(false);
        };
        const timer = setTimeout(() => done(false), ms);
        worker.addEventListener('statechange', onChange);
    });

/**
 * Cari SW baru: yang sudah menunggu, yang sedang memasang, atau minta browser
 * memeriksa sw.js terbaru lalu tunggu event `updatefound`.
 */
const findNewServiceWorker = (registration: ServiceWorkerRegistration): Promise<ServiceWorker | null> => {
    if (registration.waiting) return Promise.resolve(registration.waiting);
    if (registration.installing) return Promise.resolve(registration.installing);

    return new Promise(resolve => {
        const done = (worker: ServiceWorker | null) => {
            clearTimeout(timer);
            registration.removeEventListener('updatefound', onFound);
            resolve(worker);
        };
        const onFound = () => done(registration.installing || registration.waiting);
        const timer = setTimeout(() => done(registration.installing || registration.waiting), FIND_WORKER_TIMEOUT_MS);
        registration.addEventListener('updatefound', onFound);
        registration.update().catch(() => { /* ditangani oleh timeout */ });
    });
};

/**
 * Pasang & aktifkan SW baru, lalu tunggu sampai ia mengontrol halaman.
 * true = reload berikutnya PASTI disajikan oleh SW baru (bundle baru).
 */
const activateNewServiceWorker = async (): Promise<boolean> => {
    if (!('serviceWorker' in navigator)) return false;
    try {
        const registration = await withDeadline(navigator.serviceWorker.getRegistration(), 1500);
        if (registration === 'timeout' || !registration) return false;

        const worker = await findNewServiceWorker(registration);
        if (!worker) return false;

        // Tunggu SW baru selesai memasang (mengunduh file yang berubah saja).
        const installed = await waitForWorkerState(worker, ['installed', 'activating', 'activated'], INSTALL_TIMEOUT_MS);
        if (!installed) return false;

        // Sudah mengontrol halaman (misal diaktifkan oleh tab lain)?
        if (navigator.serviceWorker.controller === worker) return true;

        // Pasang listener SEBELUM mengirim SKIP_WAITING supaya event tidak terlewat.
        const tookControl = new Promise<boolean>(resolve => {
            const timer = setTimeout(() => resolve(false), CONTROL_TIMEOUT_MS);
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                clearTimeout(timer);
                resolve(true);
            }, { once: true });
        });
        (registration.waiting || worker).postMessage({ type: 'SKIP_WAITING' });
        return await tookControl;
    } catch (e) {
        console.warn('Gagal mengaktifkan service worker baru:', e);
        return false;
    }
};

/** Hapus semua SW + cache supaya reload mengambil semua kode dari jaringan. */
const clearAllServiceWorkersAndCaches = async (): Promise<void> => {
    // Dibatasi deadline karena unregister() dikenal menggantung di iOS.
    if ('serviceWorker' in navigator) {
        try {
            const registrations = await withDeadline(navigator.serviceWorker.getRegistrations(), 1500);
            if (registrations !== 'timeout') {
                await withDeadline(Promise.all(registrations.map(r => r.unregister())), 1500);
            }
        } catch (e) {
            // Abaikan — reload tetap berjalan
        }
    }
    if ('caches' in window) {
        try {
            const cacheNames = await withDeadline(caches.keys(), 1500);
            if (cacheNames !== 'timeout') {
                await withDeadline(Promise.all(cacheNames.map(name => caches.delete(name))), 1500);
            }
        } catch (e) {
            // Abaikan — reload tetap berjalan
        }
    }
};

/**
 * Mulai mengunduh SW baru di LATAR BELAKANG begitu rilis baru terdeteksi,
 * supaya saat user menekan "Perbarui Sekarang" file baru sudah (hampir) siap.
 */
const prefetchNewServiceWorker = (): void => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistration()
        .then(r => r?.update())
        .catch(() => { /* tidak kritis */ });
};

export const useAppVersion = (opts?: { poll?: boolean }) => {
    const { authUser } = useAuth();
    const [latestVersionData, setLatestVersionData] = useState<AppVersionRecord | null>(null);
    const [deployedInfo, setDeployedInfo] = useState<DeployedVersionInfo | null>(null);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false);
    const [isForceUpdate, setIsForceUpdate] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    // 1. Tentukan apakah ada versi baru.
    //
    // SUMBER KEBENARAN: /version.json (versi yang SUDAH ter-deploy). Dengan
    // begitu notifikasi update hanya muncul setelah kode baru benar-benar ada
    // di server — tidak bergantung admin mengisi tabel app_versions, dan tidak
    // ada notifikasi palsu bila admin merilis lebih dulu.
    // Tabel app_versions (Supabase) hanya PELENGKAP: catatan rilis + penanda
    // rilis WAJIB (`is_mandatory`).
    const fetchLatestVersion = useCallback(async () => {
        try {
            setLoading(true);
            const [deployed, releasesRes] = await Promise.all([
                fetchDeployedVersion(),
                supabase.from('app_versions').select('*').order('created_at', { ascending: false }),
            ]);

            if (releasesRes.error) {
                console.warn('Gagal mengambil data app_versions dari Supabase:', releasesRes.error.message);
            }
            const releases = (releasesRes.data || []) as AppVersionRecord[];
            setDeployedInfo(deployed);

            // Versi target: versi deploy. Cadangan (version.json tidak ada,
            // mis. mode dev): versi tertinggi di app_versions (perilaku lama).
            const target = deployed?.version
                || [...releases].sort((a, b) => compareAppVersion(b.version, a.version))[0]?.version
                || '';

            // Versi yang dianggap sudah terpasang: versi bundel, atau marker
            // "sudah diterapkan" bila lebih tinggi (mencegah modal berulang).
            const applied = getAppliedVersion();
            const installed = applied && compareAppVersion(applied, APP_VERSION) > 0 ? applied : APP_VERSION;
            const hasNewerRelease = Boolean(target && compareAppVersion(target, installed) > 0);

            const record = releases.find(r => r.version === target) || null;
            setLatestVersionData(record || (target
                ? { id: '', version: target, release_date: '', release_notes: [], is_latest: true }
                : null));
            setIsUpdateAvailable(hasNewerRelease);

            // WAJIB update hanya bila ada rilis yang SENGAJA ditandai wajib di
            // antara versi yang berjalan dan versi deploy.
            const mandatory = hasNewerRelease && releases.some(r =>
                r.is_mandatory === true
                && compareAppVersion(r.version, APP_VERSION) > 0
                && compareAppVersion(r.version, target) <= 0
            );
            setIsForceUpdate(mandatory);

            if (hasNewerRelease) prefetchNewServiceWorker();
        } catch (err) {
            console.error('Error fetching app version:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // 2. Sinkronkan versi aplikasi user ke Firestore (user_permissions) secara efisien (throttled)
    const syncUserVersion = useCallback(async () => {
        if (!authUser?.email) return;

        try {
            const emailClean = authUser.email.toLowerCase().trim();
            const q = query(collection(db, 'user_permissions'), where('email', '==', emailClean));
            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                const docSnap = snapshot.docs[0];
                const userData = docSnap.data();
                const todayStr = new Date().toISOString().split('T')[0];

                const currentRecordedVersion = userData.appVersion;
                const lastRecordedActive = userData.lastActiveAt ? userData.lastActiveAt.split('T')[0] : '';

                // Hanya jalankan update jika versi belum dicatat ATAU keaktifan belum dicatat hari ini
                if (currentRecordedVersion !== APP_VERSION || lastRecordedActive !== todayStr) {
                    await updateDoc(doc(db, 'user_permissions', docSnap.id), {
                        appVersion: APP_VERSION,
                        lastActiveAt: new Date().toISOString()
                    });
                }

                // Marker "versi terpasang" disetel HANYA setelah penulisan ke
                // Firestore sukses, dan hanya DIAIKKAN (tidak pernah diturunkan).
                // Kalau user baru saja menyetujui versi yang lebih baru dari
                // bundle yang sedang berjalan (deploy belum sampai), marker tetap
                // bertahan supaya modal tidak muncul berulang tanpa henti.
                if (compareAppVersion(APP_VERSION, getAppliedVersion()) > 0) {
                    setAppliedVersion(APP_VERSION);
                }
            }
        } catch (err) {
            console.error('Gagal menyinkronkan versi user ke user_permissions:', err);
        }
    }, [authUser]);

    // 3. Fungsi pembaruan aplikasi & bersihkan cache Service Worker
    //
    // PRINSIP PENTING: pembersihan (skipWaiting / unregister / hapus cache)
    // boleh gagal atau lambat, tetapi RELOAD TIDAK BOLEH TERTUNDA SELAMANYA. Di
    // beberapa perangkat (iOS Safari standalone, jaringan lambat) promise
    // unregister() bisa menggantung — maka tiap langkah dibatasi deadline, plus
    // failsafe timer yang memaksa reload apa pun yang terjadi.
    //
    // DUA MODE:
    //  - Mode update (default, clearCache=false) — dipakai modal "Perbarui
    //    Sekarang". JALUR CEPAT & PASTI dalam SEKALI klik:
    //      a. Cari Service Worker (SW) BARU: yang sudah menunggu (waiting), yang
    //         sedang memasang (installing), atau minta browser memeriksa update.
    //      b. TUNGGU SW baru selesai memasang. Workbox hanya mengunduh file yang
    //         BERUBAH (file lain dipakai ulang dari cache), jadi biasanya cepat.
    //         Dulu langkah ini hanya ditunggu ±3 detik: di jaringan seluler SW
    //         baru belum selesai memasang, reload dilayani SW LAMA (bundle lama,
    //         versi tidak naik), lalu modal muncul LAGI setelah SW baru siap →
    //         user harus klik "Perbarui" dua kali.
    //      c. Aktifkan SW baru (SKIP_WAITING), tunggu ia MENGONTROL halaman,
    //         baru reload → reload pasti menyajikan bundle baru.
    //      d. Jika SW baru tidak ditemukan / gagal / kehabisan waktu: fallback
    //         hapus semua SW + cache, sehingga reload mengambil bundle terbaru
    //         langsung dari jaringan (lebih lambat, tapi tetap SEKALI klik).
    //  - Mode bersih total (clearCache=true) — tombol "Perbarui Aplikasi" di
    //    halaman Profil: semua cache dihapus + SW di-unregister, lalu seluruh
    //    kode aplikasi diunduh ulang (antisipasi aplikasi usang / error).
    const handleForceUpdateApp = useCallback(async (opts?: { clearCache?: boolean }) => {
        const clearCache = opts?.clearCache === true;

        // Tandai versi yang akan di-install sebagai "sudah diterapkan" SEBELUM
        // reload (sinkron — tidak mungkin menggantung), supaya kode lama tidak
        // sempat menulis versi lama ke Firestore saat reload berlangsung.
        // Marker hanya NAIK, tidak pernah turun (dibandingkan numerik).
        const upcomingVersion = latestVersionData?.version || APP_VERSION;
        if (compareAppVersion(upcomingVersion, getAppliedVersion()) > 0) {
            setAppliedVersion(upcomingVersion);
        }

        let reloaded = false;
        const reloadNow = () => {
            if (reloaded) return; // Cegah reload ganda (failsafe + finally)
            reloaded = true;
            try {
                window.location.reload();
            } catch (e) {
                // Fallback terakhir (misal lokasi di-override PWA shell)
                try { window.location.href = window.location.href; } catch { /* abaikan */ }
            }
        };

        // Failsafe mutlak: apa pun yang terjadi, reload pasti terjadi.
        // Jalur normal selesai jauh lebih cepat; batas ini hanya jaring pengaman
        // (cukup panjang untuk mengunduh file yang berubah di jaringan seluler).
        const failsafeTimer = setTimeout(reloadNow, UPDATE_FAILSAFE_MS);

        try {
            if (clearCache) {
                await clearAllServiceWorkersAndCaches();
            } else {
                const activated = await activateNewServiceWorker();
                if (!activated) {
                    // Tidak ada SW baru yang bisa diaktifkan (belum terdeteksi,
                    // gagal memasang, atau kehabisan waktu) → ambil bundle
                    // terbaru langsung dari jaringan supaya versi PASTI naik.
                    await clearAllServiceWorkersAndCaches();
                }
            }

            // Hapus storage ringan versi lama yang sudah tidak relevan
            try {
                sessionStorage.removeItem('app_update_dismissed');
                sessionStorage.removeItem('app_update_modal_dismissed');
            } catch (e) {
                // Abaikan kegagalan storage
            }
        } catch (err) {
            console.error('Gagal memperbarui aplikasi:', err);
        } finally {
            clearTimeout(failsafeTimer);
            reloadNow();
        }
    }, [latestVersionData]);

    useEffect(() => {
        fetchLatestVersion();
        syncUserVersion();
    }, [fetchLatestVersion, syncUserVersion]);

    // Cek ulang versi deploy saat aplikasi kembali dibuka (dari latar belakang)
    // dan berkala, supaya pengguna yang membiarkan aplikasi terbuka lama tetap
    // mendapat notifikasi. Hanya untuk komponen yang selalu terpasang (ReloadPrompt).
    const poll = opts?.poll === true;
    useEffect(() => {
        if (!poll) return;
        const onVisible = () => {
            if (document.visibilityState === 'visible') fetchLatestVersion();
        };
        document.addEventListener('visibilitychange', onVisible);
        const iv = setInterval(fetchLatestVersion, VERSION_POLL_INTERVAL_MS);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(iv);
        };
    }, [poll, fetchLatestVersion]);

    return {
        currentVersion: APP_VERSION,
        buildId: BUILD_ID,
        deployedVersion: deployedInfo?.version || null,
        deployedBuildId: deployedInfo?.buildId || null,
        latestVersionData,
        isUpdateAvailable,
        isForceUpdate,
        loading,
        fetchLatestVersion,
        handleForceUpdateApp,
        formatVersion
    };
};
